import React, { useEffect, useState, useCallback, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Pagination from "react-js-pagination";
import Select from "react-select";
import ReactDatetime from "react-datetime";
import ReactBSAlert from "react-bootstrap-sweetalert";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  UncontrolledTooltip,
  CardHeader,
  CardBody,
  Form,
  CardTitle,
  Input,
  Table,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import subscriptionsService from "../../services/api/subscription-membershipl-service";
import projectUsersService from "../../services/api/project-users-service";
import ProjectContext from "../../ProjectContext.js";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
const RECORDS_PER_PAGE = 10;

const STATUS_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "authenticated", label: "Authenticated" },
  { value: "activated", label: "Activated" },
  { value: "charged", label: "Charged" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "halted", label: "Halted" },
  { value: "cancelled", label: "Cancelled" },
  { value: "paused", label: "Paused" },
  { value: "resumed", label: "Resumed" },
];


const TYPE_OPTIONS = [
  { value: "onetime", label: "One time" },
  { value: "recurring", label: "Recurring" },
];


const PLATFORM_OPTIONS = [
  { value: "web", label: "Web" },
  { value: "android", label: "Android" },
  { value: "iOS", label: "IOS" },
];

const initialFormState = {
  user_id: "",
  subscription_id: "",
  from_date: "",
  to_date: "",
  start_at: "",
  end_at: "",
  status: ['authenticated','activated','charged'],
  platform: [],
  payment_gateway: [],
  type : "",
  plan_id : [],
  paid_count: null
};

const Subscriptions = () => {
  const history = useHistory();
  const location = useLocation();
  const projectDetails = useContext(ProjectContext);
  const projectId =
    projectDetails.id || new URLSearchParams(location.search).get("projectid");
  if (projectDetails.id == "") {
    history.push("/project/allprojects");
  }

  const [subscriptions, setSubscriptions] = useState([]);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [gateways, setGateways] = useState([]);
    const [planId, setPlanId] = useState([]);

  const initFromURL = () => {
    const urlParams = new URLSearchParams(location.search);

    const formFromURL = { ...initialFormState };

    Object.keys(initialFormState).forEach((key) => {
      const paramValue = urlParams.get(key);

      if (paramValue !== null) {
        if (Array.isArray(initialFormState[key])) {
          formFromURL[key] = paramValue ? paramValue.split(",") : [];
        } else {
          formFromURL[key] = paramValue;
        }
      }
    });

    formFromURL.project_id = projectId || "";

    return formFromURL;
  };

  const [formState, setFormState] = useState(initFromURL);

  const [activePage, setActivePage] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return parseInt(urlParams.get("page") || "1", 10);
  });

  const updateURL = useCallback(() => {
    const params = new URLSearchParams();

    if (projectId) {
      params.set("projectid", projectId);
    }

    Object.entries(formState).forEach(([key, value]) => {
      if (key !== "project_id") {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(","));
        } else if (value && !Array.isArray(value)) {
          params.set(key, value);
        }
      }
    });

    if (activePage > 1) {
      params.set("page", activePage.toString());
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [formState, activePage, projectId, history, location.pathname]);

  useEffect(() => {
    updateURL();
  }, [formState, activePage, updateURL]);

  const handleClickUser = useCallback(
    (projectId, userId) => {
      if (!projectId || !userId) return;

      history.push(
        `/dashboard/user-detailed-view?userid=${userId}&projectid=${projectId}`
      );
    },
    [history]
  );

  const handleClickSubs = useCallback(
    (projectId, pg_subscription_id) => {
      if (!projectId || !pg_subscription_id) return;

      history.push(
        `/membership/transaction?pg_subscription_id=${pg_subscription_id}&projectid=${projectId}`
      );
    },
    [history]
  );

  const fetchGateways = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await subscriptionsService.getAllPaymentGateways();
      if (response.status === true && response.data) {
        const formattedGateways = response.data.map((gateway) => ({
          value: gateway,
          label: gateway
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        }));
        setGateways(formattedGateways);
      }
    } catch (error) {
      console.error("Error fetching gateways:", error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchGateways();
  }, [fetchGateways]);


  const fetchPlanId = useCallback(async () => {
      if (!projectId) return;
      try {
        const response = await subscriptionsService.getAllPlanId();
        if (response.status === true && response.data) {
          const formattedPlanId = response.data.map((planId) => ({
            value: planId,
            label: planId
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
          }));
          setPlanId(formattedPlanId);
        }
      } catch (error) {
        console.error("Error fetching gateways:", error);
      }
    }, [projectId]);
  
    useEffect(() => {
      fetchPlanId();
    }, [fetchPlanId]);
  

  const debouncedUserFetch = debounce(async (inputValue, callback, projectId, setUserOptions) => {
    try {
      const users = await projectUsersService.fetchUsers({
        project_id: projectId,
        search: inputValue,
        page: 1,
        page_size: "",
      });

      if (users?.status && users?.data?.users) {
        const options = users.data.users.map((user) => ({
          value: user.user_id,
          label: user.email
            ? `${user.name} (${user.email})`
            : `${user.name} ${user.phone_number}`,
          email: user.email,
        }));

        setUserOptions(options);  // Save for value rendering
        callback(options);        // Resolve for AsyncSelect
      } else {
        setUserOptions([]);
        callback([]);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setUserOptions([]);
      callback([]);
    }
  }, 500);


  const fetchUserOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (!projectId) return resolve([]);
        debouncedUserFetch(inputValue, resolve, projectId, setUserOptions);
      }),
    [projectId, setUserOptions]
  );


  const fetchSubscriptions = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const response = await subscriptionsService.getSubscriptions(
        formState,
        activePage,
        RECORDS_PER_PAGE
      );

      if (response.status && response.data) {
        setSubscriptions(response.data.subscriptions);
        setTotalSubscriptions(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setAlert(createAlert("error", "Failed to fetch subscriptions"));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, formState, activePage]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterReset = useCallback(() => {
    setFormState({ ...initialFormState, project_id: projectId });
    setActivePage(1);

    const params = new URLSearchParams();
    if (projectId) {
      params.set("projectid", projectId);
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [projectId, history, location.pathname]);

  const handleFilterSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setActivePage(1);
      fetchSubscriptions();
    },
    [fetchSubscriptions]
  );

  const handlePageChange = useCallback((pageNumber) => {
    setActivePage(pageNumber);
  }, []);

  const createAlert = useCallback((type, message) => {
    const alertProps = {
      style: { display: "block", marginTop: "-100px" },
      onConfirm: () => setAlert(null),
      onCancel: () => setAlert(null),
    };

    switch (type) {
      case "loading":
        return (
          <ReactBSAlert
            info
            {...alertProps}
            showConfirm={false}
            title="Processing..."
          >
            <div className="d-flex flex-column align-items-center">
              <Spinner className="mb-3" />
              <p>{message}</p>
            </div>
          </ReactBSAlert>
        );
      case "success":
        return (
          <ReactBSAlert
            success
            {...alertProps}
            title="Success"
            confirmBtnBsStyle="success"
          >
            {message}
          </ReactBSAlert>
        );
      case "error":
        return (
          <ReactBSAlert
            danger
            {...alertProps}
            title="Error"
            confirmBtnBsStyle="danger"
          >
            {message}
          </ReactBSAlert>
        );
      default:
        return null;
    }
  }, []);

  if (!projectId) {
    history.push("/project/allprojects");
  }

  return (
    <div className="content mt-30">
      {alert}
      <Container>
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <CardTitle tag="h4">Membership Dashboard</CardTitle>
                  <div className="text-muted">
                    Project: <strong>{projectDetails.name}</strong>
                  </div>
                </div>

                <Form onSubmit={handleFilterSubmit}>
                  <Row>
                    <Col md="3">
                      <AsyncSelect
                        isClearable
                        placeholder="Select User"
                        defaultOptions={false}
                        loadOptions={fetchUserOptions}
                        onChange={(option) =>
                          handleFormChange("user_id", option?.value || "")
                        }
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? "No users found" : "Search to get options"
                        }
                        value={
                          formState.user_id
                            ? {
                              value: formState.user_id,
                              label:
                                userOptions.find((u) => u.value === formState.user_id)?.label ||
                                "Selected User",
                            }
                            : null
                        }
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.from_date}
                        onChange={(date) =>
                          handleFormChange("from_date", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Created At : From Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.to_date}
                        onChange={(date) =>
                          handleFormChange("to_date", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Created At : To Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="text"
                        placeholder="Membership ID"
                        value={formState.subscription_id}
                        onChange={(e) =>
                          handleFormChange("subscription_id", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.filter((option) =>
                          formState.status.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "status",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Platform"
                        options={PLATFORM_OPTIONS}
                        value={PLATFORM_OPTIONS.filter((option) =>
                          formState.platform.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "platform",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Select
                        isMulti
                        isClearable
                        placeholder="Select Payment Gateway"
                        options={gateways}
                        value={gateways.filter((option) =>
                          formState.payment_gateway.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                          handleFormChange(
                            "payment_gateway",
                            selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : []
                          )
                        }
                      />
                    </Col>
                    <Col md="3">
                  <Select
                    isClearable
                    placeholder="Membership Type"
                    options={TYPE_OPTIONS}
                    value={
                      TYPE_OPTIONS.find(
                        (option) => option.value === formState.type
                      ) || null
                    }
                    onChange={(option) =>
                      handleFormChange("type", option?.value)
                    }
                  />
                </Col>
                  </Row>

                  <Row className="mt-3">

                     <Col md="3">
                                          <Select
                                            isMulti
                                            isClearable
                                            placeholder="Select Plan Id"
                                            options={planId}
                                            value={planId.filter((option) =>
                                              formState.plan_id.includes(option.value)
                                            )}
                                            onChange={(selectedOptions) =>
                                              handleFormChange(
                                                "plan_id",
                                                selectedOptions
                                                  ? selectedOptions.map((opt) => opt.value)
                                                  : []
                                              )
                                            }
                                          />
                                        </Col>
                  <Col md="3">
                      <ReactDatetime
                        value={formState.start_at}
                        onChange={(date) =>
                          handleFormChange("start_at", date._d)
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "Start At",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.end_at}
                        onChange={(date) => handleFormChange("end_at", date._d)}
                        inputProps={{
                          className: "form-control",
                          placeholder: "End At",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        type="number"
                        placeholder="Paid Count"
                        value={formState?.paid_count || ""}
                        onChange={(e) => handleFormChange("paid_count", e.target.value)}
                      />
                    </Col>
                    <Col md="6">
                      <Button
                        className="btn-round mr-2"
                        color="info"
                        type="submit"
                      >
                        Search
                      </Button>
                      <Button
                        className="btn-round"
                        color="warning"
                        type="button"
                        onClick={handleFilterReset}
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>
                </Form>
                {totalSubscriptions > 0 ? (
                  <Row>
                    <Col md="6">
                      <div style={{ float: "right", margin: "5px" }}>
                        <b>
                          {" "}
                          {(activePage - 1) * RECORDS_PER_PAGE + 1} -{" "}
                          {Math.min(
                            activePage * RECORDS_PER_PAGE,
                            totalSubscriptions
                          )}{" "}
                          of {totalSubscriptions}
                        </b>
                      </div>
                    </Col>
                    <Col md="6">
                      <div style={{ float: "left" }}>
                        <Pagination
                          activePage={activePage}
                          itemsCountPerPage={RECORDS_PER_PAGE}
                          totalItemsCount={totalSubscriptions}
                          pageRangeDisplayed={3}
                          onChange={handlePageChange}
                          itemClass="page-item"
                          linkClass="page-link"
                        />
                      </div>
                    </Col>
                  </Row>
                ) : null}
              </CardHeader>

              <CardBody>
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>S.no.</th>
                      <th>User Email or Phone No.</th>
                      <th>Membership ID</th>
                      <th>Paid Count</th>
                      <th>Type / Payment Gateway </th>
                      <th>Status / Plan ID / Amount</th>
                      <th>Created At</th>
                      <th>Date</th>
                      <th>Current Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <Spinner />
                        </td>
                      </tr>
                    ) : subscriptions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <CardTitle tag="h4">No Records Found</CardTitle>
                        </td>
                      </tr>
                    ) : (
                      subscriptions.map((subscription, index) => (
                        <tr key={subscription._id}>
                          <td>
                            {(activePage - 1) * RECORDS_PER_PAGE + index + 1}
                          </td>
                          <td
                            style={{ cursor: "pointer" }}
                            className="text-left "
                            onClick={() =>
                              handleClickUser(
                                projectId,
                                subscription?.user_id?._id
                              )
                            }
                          >
                          {subscription?.user_id?.email ||
                            subscription?.user_id?.phone_number ? (
                              <>
                                {subscription?.user_id?.email && (
                                  <div>{subscription.user_id.email}</div>
                                )}
                                {subscription?.user_id?.phone_number && (
                                  <div>{subscription.user_id.phone_number}</div>
                                )}
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            style={{ cursor: "pointer", color: "#0077b6" }}
                            onClick={() =>
                              handleClickSubs(
                                projectId,
                                subscription?.pg_subscription_id
                              )
                            }
                          >
                            {subscription.pg_subscription_id || ""}
                          </td>

                          <td>{subscription.paid_count}</td>
                          <td>
                            <div>
                              {subscription?.type
                                ? subscription.type === "onetime"
                                  ? "One Time"
                                  : "Recurring"
                                : subscription?.total_count === 1
                                ? "One Time"
                                : subscription?.total_count
                                ? "Recurring"
                                : "N/A"}
                            </div>
                            <div>{subscription.payment_gateway}</div>
                          </td>
                          <td>
                            <div>{subscription.status}</div>
                            <div>{subscription.plan_id}</div>
                            <div className="text-left">{subscription?.currency === "INR" ? "₹" : "$"}{(subscription?.amount || 0) / 100}</div>
                            </td>
                         
                          <td>
                            {subscription?.createdAt
                              ? moment(subscription?.createdAt).format(
                                  "DD MMM YYYY, hh:mm A"
                                )
                              : ""}
                          </td>
                          <td>
                            <div>
                              {subscription?.start_at ? (
                                <>
                                  <strong>Start At:</strong>{" "}
                                  {moment
                                    .unix(subscription.start_at)
                                    .format("DD MMM YYYY, hh:mm A")}
                                </>
                              ) : null}
                            </div>
                            <div>
                              {subscription?.end_at ? (
                                <>
                                  <strong>End At:</strong>{" "}
                                  {moment
                                    .unix(subscription.end_at)
                                    .format("DD MMM YYYY, hh:mm A")}
                                </>
                              ) : null}
                            </div>
                            {!subscription?.start_at &&
                              !subscription?.end_at && <>-</>}
                          </td>

                          <td>
                            <div>
                              {subscription?.current_start ? (
                                <>
                                  <strong>Current Start:</strong>{" "}
                                  {moment
                                    .unix(subscription.current_start)
                                    .format("DD MMM YYYY, hh:mm A")}
                                </>
                              ) : null}
                            </div>
                            <div>
                              {subscription?.current_end ? (
                                <>
                                  <strong>Current End:</strong>{" "}
                                  {moment
                                    .unix(subscription.current_end)
                                    .format("DD MMM YYYY, hh:mm A")}
                                </>
                              ) : null}
                            </div>

                            {!subscription?.current_start &&
                              !subscription?.current_end && <>-</>}
                          </td>

                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {/* Info Button with Tooltip - unique ID with subscription ID */}
                              <Button
                                id={`info-tooltip-${subscription._id}`}
                                color="info"
                                size="sm"
                                onClick={() =>
                                  history.push(
                                    `/membership/subscription-details/${subscription._id}`
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faInfo} />
                              </Button>
                              <UncontrolledTooltip
                                placement="top"
                                target={`info-tooltip-${subscription._id}`}
                              >
                                View subscription details
                              </UncontrolledTooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Subscriptions;
